import {
  Dispatch,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from "react";

function readValue<T>(key: string, initialValue?: T): T | undefined {
  if (typeof window === "undefined") {
    return initialValue;
  }

  try {
    const raw = window.localStorage.getItem(key);
    return raw === null ? initialValue : (JSON.parse(raw) as T);
  } catch {
    return initialValue;
  }
}

/**
 * Drop-in replacement for `react-use`'s `useLocalStorage`.
 * Persists a JSON-serialized value to `localStorage` under `key`.
 * Returns `[value, setValue, remove]`.
 */
export function useLocalStorage<T>(
  key: string,
  initialValue?: T,
): [T | undefined, Dispatch<SetStateAction<T | undefined>>, () => void] {
  const [value, setValueState] = useState<T | undefined>(() =>
    readValue(key, initialValue),
  );

  const setValue: Dispatch<SetStateAction<T | undefined>> = useCallback(
    (next) => {
      setValueState((prev) => {
        const resolved =
          next instanceof Function
            ? (next as (prev: T | undefined) => T | undefined)(prev)
            : next;

        try {
          if (resolved === undefined) {
            window.localStorage.removeItem(key);
          } else {
            window.localStorage.setItem(key, JSON.stringify(resolved));
          }
        } catch {
          // ignore write errors (private mode, quota, etc.)
        }

        return resolved;
      });
    },
    [key],
  );

  const remove = useCallback(() => {
    try {
      window.localStorage.removeItem(key);
    } catch {
      // ignore
    }
    setValueState(undefined);
  }, [key]);

  return useMemo(() => [value, setValue, remove], [value, setValue, remove]);
}
